import { Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { map } from 'rxjs/operators';
import { RouterStateService } from '../../../../../core/routing/router-state.service';
import { FeatureService, PermissionService } from '../../../../../core/services/permission';
import { IntegrationProvider } from '../../../../../data/models/integrations.model';
import { RepoSettingsService } from '../../../../../data/repositories/repo-settings.service';
import { AccountIntegrationsStoresService } from '../../../../account';
import { Sidebar2SubpanelSharedService } from '../../sidebar2-subpanel-shared.service';
import { Sidebar2SubpanelStateService } from '../../sidebar2-subpanel-state.service';

@Component({
    selector: 'app-sidebar2-jobs-sub-menu',
    templateUrl: './sidebar2-jobs-sub-menu.component.html',
    standalone: false,
})
export class Sidebar2JobsSubMenuComponent implements OnInit {
    #accountIntegrationsStoresService = inject(AccountIntegrationsStoresService);
    #featureService = inject(FeatureService);
    #permissionService = inject(PermissionService);
    isImportedBillsVisible? = false;
    hasSupportedAccountingIntegration$ = this.#accountIntegrationsStoresService.getAccountingIntegration$.pipe(
        map((integration) => integration?.provider === IntegrationProvider.Xero || integration?.provider === IntegrationProvider.QuickBooks)
    );
    statuses = [];
    settingsProperty = 'NavigationV2JobsStatusSelections'; // from user settings
    toggleJobs = false;

    orderStatus = [];
    orderSettingsProp = 'NavigationV2JobsOrderStatusSelections';
    toggleOrder = false;

    invoiceStatus = [];
    invoiceSettingsProp = 'NavigationV2JobsInvoiceStatusSelections';
    toggleInvoice = false;

    importedBillsSelected = false;
    importTimesheetsSelected = false;

    DELIMITER = '|';
    constructor(
        private subpanelService: Sidebar2SubpanelStateService,
        private sidebar2SharedService: Sidebar2SubpanelSharedService,
        public settings: RepoSettingsService,
        private routerStateService: RouterStateService,
        public router: Router
    ) {
        this.isImportedBillsVisible =
            this.#featureService.isStateAvailable('jobs.all-invoices') &&
            this.#featureService.isStateAvailable('jobs.view.invoices') &&
            this.#permissionService.isAvailable('jobs.maintain-invoices.view-invoice-details') &&
            this.#permissionService.isAvailable('jobs.maintain-purchase-orders.create-purchase-order');
    }

    ngOnInit() {
        const route = this.routerStateService.getCurrentUrlRoute();
        switch (route) {
            case '/jobs/orders':
                this.getOrderStatuses();
                break;
            case '/jobs/invoices':
                this.getInvoiceStatuses();
                break;
            case '/jobs/accounting/import-bills':
                this.selectImportedBills();
                break;
            default:
                this.getJobStatuses();
                break;
        }
    }

    getHeaderLink(submenu: string) {
        if (submenu === 'orders' && this.routerStateService.currentRoute?.queryParams?.submenu !== 'orders') {
            this.getOrderStatuses();
            const destRoute = '/jobs/orders';
            const destinationParams = { status: '', submenu: 'orders', subpanel: 'true' };
            destinationParams.status = this.getStatus(this.orderSettingsProp, this.orderStatus);
            this.router.navigate([destRoute], { queryParams: destinationParams, queryParamsHandling: 'replace' });
        } else if (submenu === 'invoices' && this.routerStateService.currentRoute?.queryParams?.submenu !== 'invoices') {
            this.getInvoiceStatuses();
            const destRoute = '/jobs/invoices';
            const destinationParams = { status: '', submenu: 'invoices', subpanel: 'true' };
            destinationParams.status = this.getStatus(this.invoiceSettingsProp, this.invoiceStatus);
            this.router.navigate([destRoute], { queryParams: destinationParams, queryParamsHandling: 'replace' });
        } else if (submenu === 'orders' && this.routerStateService.currentRoute?.queryParams?.submenu === 'orders')
            this.toggleOrder = !this.toggleOrder;
        else if (submenu === 'invoices' && this.routerStateService.currentRoute?.queryParams?.submenu === 'invoices')
            this.toggleInvoice = !this.toggleInvoice;
        else if (submenu === 'imported-bills') {
            const destRoute = '/jobs/accounting/import-bills';
            const destinationParams = { submenu: 'imported-bills', subpanel: 'true' };
            this.router.navigate([destRoute], { queryParams: destinationParams, queryParamsHandling: 'replace' });
            this.selectImportedBills();
        } else if (submenu === 'import-timesheets') {
            this.getImportedTimsheetsStatuses();
            const destRoute = '/jobs/import-timesheets';
            const destinationParams = { submenu: 'imported-timesheets', subpanel: 'false' };
            this.router.navigate([destRoute], { queryParams: destinationParams, queryParamsHandling: 'replace' });
        }
    }

    // @ts-ignore TS7006
    getStatus(prop, list) {
        let status;
        // @ts-ignore TS7052
        if (this.settings[prop] && this.settings[prop].length > 0) {
            // @ts-ignore TS7052
            status = this.settings[prop];
            // @ts-ignore TS7006
        } else status = list.map((s) => s.Status.toLowerCase().replace(/ /g, '')).join('|');
        return status;
    }

    getJobStatuses() {
        this.subpanelService.getJobsPromise().then((res) => {
            this.statuses = res;
            this.toggleJobs = true;
        });
    }

    getOrderStatuses() {
        this.toggleOrder = true;
        this.toggleInvoice = false;
        this.toggleJobs = false;
        this.importTimesheetsSelected = false;
        this.importedBillsSelected = false;
        this.statuses.length = 0;
        this.invoiceStatus.length = 0;
        const orderStatus = this.sidebar2SharedService.resetOrderCounts();
        // @ts-ignore TS2322
        this.orderStatus = Object.keys(orderStatus).map((key) => {
            // @ts-ignore TS7053
            return orderStatus[key];
        });
    }

    getInvoiceStatuses() {
        this.toggleOrder = false;
        this.toggleInvoice = true;
        this.toggleJobs = false;
        this.importTimesheetsSelected = false;
        this.importedBillsSelected = false;
        this.statuses.length = 0;
        this.orderStatus.length = 0;
        const invoiceStatus = this.sidebar2SharedService.resetInvoiceCounts();
        // @ts-ignore TS2322
        this.invoiceStatus = Object.keys(invoiceStatus).map((key) => {
            // @ts-ignore TS7053
            return invoiceStatus[key];
        });
    }

    // PN - I agree with SK below. Have to do it this way to avoid breaking changes
    getImportedTimsheetsStatuses() {
        this.importTimesheetsSelected = true;
        this.invoiceStatus.length = 0;
        this.orderStatus.length = 0;
    }

    // SK: This hurts me in my soul but there is zero chance I'm refactoring this
    selectImportedBills() {
        this.statuses.length = 0;
        this.toggleOrder = false;
        this.orderStatus.length = 0;
        this.toggleInvoice = false;
        this.invoiceStatus.length = 0;
        this.importedBillsSelected = true;
    }
}
